import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Image, Form, Spinner, Button } from 'react-bootstrap';
import Loader from '../../Utility/Loader';

import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap-icons/font/bootstrap-icons.css';
import './email-verify.css';

import logo2 from "../../../assets/images/logo2.png";
import backarrow from "../../../assets/images/backarrow.jpg";
import responsivebg from "../../../assets/images/responsive-bg.png";

import Stepper from '../Stepper';
import * as userActions from "../../../actions/user";
import * as authActions from "../../../actions/auth";
import { useProfile } from "../../userflow/components/context/ProfileContext";
import { handleUserNavigation } from "../../../utils/handleUserRedirect";

// Email validation utility
const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
};

export const EmailVerify = () => {
    const navigate = useNavigate();
    const pollInterval = useRef(null);

    const [email, setEmail] = useState('');
    const [confirmEmail, setConfirmEmail] = useState('');
    const [isVerified, setIsVerified] = useState(false);
    const [verificationSent, setVerificationSent] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [loading, setLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [resendCooldown, setResendCooldown] = useState(0);

    const { fetchProfile } = useProfile();
    const [testUrl, setTestUrl] = useState('');

    // Check verification status
    const checkStatus = useCallback(async () => {
        const response = await authActions.checkEmailVerification();
        if (response && response.status && response.emailVerified) {
            setIsVerified(true);
            const profileRes = await fetchProfile();
            if (profileRes && profileRes.ok) {
                handleUserNavigation(profileRes.data, navigate, () => {}, 'home');
            } else {
                navigate("/getstarted");
            }
            return true;
        }
        return false;
    }, [navigate, fetchProfile]);

    // Fetch initial data
    useEffect(() => {
        const init = async () => {
            try {
                setLoading(true);
                const emailResponse = await userActions.getEmailDetail();
                
                if (emailResponse && emailResponse.email) {
                    setEmail(emailResponse.email);
                    setConfirmEmail(emailResponse.email);
                    
                    // If email exists, check if it's verified
                    const verified = await checkStatus();
                    if (!verified) {
                        setVerificationSent(true);
                    }
                }
            } catch (error) {
                console.error("Error during initialization:", error);
            } finally {
                setLoading(false);
            }
        };

        init();
        return () => {
            if (pollInterval.current) clearInterval(pollInterval.current);
        };
    }, [checkStatus]);

    // Polling for verification status when screen is "Sent"
    useEffect(() => {
        if (verificationSent && !isVerified) {
            pollInterval.current = setInterval(checkStatus, 5000);
        } else {
            if (pollInterval.current) clearInterval(pollInterval.current);
        }
        return () => {
            if (pollInterval.current) clearInterval(pollInterval.current);
        };
    }, [verificationSent, isVerified, checkStatus]);

    // Resend cooldown timer
    useEffect(() => {
        if (resendCooldown > 0) {
            const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 2000);
            return () => clearTimeout(timer);
        }
    }, [resendCooldown]);

    const handleSendVerification = async (e) => {
        if (e) e.preventDefault();
        
        if (!validateEmail(email)) {
            setErrorMessage('Please enter a valid email address');
            return;
        }
        if (email.toLowerCase().trim() !== confirmEmail.toLowerCase().trim()) {
            setErrorMessage('Email addresses do not match');
            return;
        }

        setIsSubmitting(true);
        setErrorMessage('');

        try {
            // 1. Update/Save email in profile
            await userActions.updateEmail(JSON.stringify({ email: email.trim() }));
            
            // 2. Trigger verification email
            const result = await authActions.sendVerificationEmail();
            if (result && result.status) {
                setVerificationSent(true);
                setResendCooldown(60); // 1 minute cooldown
                if (!process.env.NODE_ENV || process.env.NODE_ENV !== 'production') {
                    if (result.url) {
                        setTestUrl(result.url);
                    }
                }
            } else {
                setErrorMessage(result?.message || 'Failed to send verification email.');
            }
        } catch (error) {
            setErrorMessage(error.message || 'An error occurred. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleResend = async () => {
        if (resendCooldown > 0) return;
        
        setIsSubmitting(true);
        try {
            const result = await authActions.sendVerificationEmail();
            if (result && result.status) {
                setResendCooldown(60);
                if (!process.env.NODE_ENV || process.env.NODE_ENV !== 'production') {
                    if (result.url) {
                        setTestUrl(result.url);
                        console.log("Verification URL (Testing):", result.url);
                    }
                }
            } else {
                setErrorMessage(result?.message || 'Failed to resend email.');
            }
        } catch (error) {
            setErrorMessage('Error resending email. Please try again later.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleBackNavigation = () => {
        if (verificationSent) {
            setVerificationSent(false);
        } else {
            window.history.back();
        }
    };

    if (loading) return <Loader />;

    return (
        <div className='emailverify-container' role="main">
            <div className='emailverify-bg'>
                <Image className='responsive-bg' src={responsivebg} alt="Background" />
            </div>

            <Container className='emailverify-main'>
                <Container className='emailverify-box'>
                    <div className='logo-progressbar3'>
                        <div className='logo-arrow3'>                            
                            <Image src={logo2} alt="Logo" className='logo' />
                        </div>
                        <Stepper activeStep="Email" />
                    </div>

                    {!verificationSent ? (
                        <>
                            <Container>
                                <h3>Enter your email</h3>
                                <p className="text-muted">We'll send a verification link to this address.</p>
                            </Container>

                            <Form onSubmit={handleSendVerification} className='emailverify-form'>
                                <Form.Group className='mb-3'>
                                    <Form.Label>Email</Form.Label>
                                    <Form.Control
                                        type="email"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        placeholder="example@example.com"
                                        disabled={isSubmitting}
                                    />
                                </Form.Group>
                                <Form.Group className='mb-4'>
                                    <Form.Label>Confirm Email</Form.Label>
                                    <Form.Control
                                        type="email"
                                        value={confirmEmail}
                                        onChange={(e) => setConfirmEmail(e.target.value)}
                                        placeholder="example@example.com"
                                        disabled={isSubmitting}
                                    />
                                </Form.Group>

                                {errorMessage && <div className="text-danger mb-3">*{errorMessage}</div>}

                                <div className="d-flex justify-content-center email_verify_next_btn">
                                    <button type="submit" className='global-next-bottom-fix-btn' disabled={isSubmitting}>
                                        {isSubmitting ? <Spinner animation="border" size="sm" /> : "Send Link"}
                                    </button>
                                </div>
                            </Form>
                        </>
                    ) : (
                        <Container className="text-center py-4">
                            <div className="mb-4">
                                <i className="bi bi-envelope-check" style={{ fontSize: '3rem', color: '#8e24aa' }}></i>
                            </div>
                            <h3>Check your inbox!</h3>
                            <p>
                                We've sent a verification link to <strong>{email}</strong>.
                                Please click the link in the email to continue.
                            </p>
                            
                            <div className="mt-5 d-grid gap-3">
                                <div className="d-flex justify-content-center email_verify_next_btn">
                                    <button 
                                        className='global-next-bottom-fix-btn' 
                                        onClick={handleResend} 
                                        disabled={isSubmitting || resendCooldown > 0}
                                    >
                                        {isSubmitting ? <Spinner animation="border" size="sm" /> : 
                                         resendCooldown > 0 ? `Resend in ${resendCooldown}s` : "Resend Link"}
                                    </button>
                                </div>
                                
                                <Button 
                                    variant="link" 
                                    className="text-decoration-none mt-2" 
                                    onClick={() => {
                                        setVerificationSent(false);
                                        setTestUrl('');
                                    }}
                                    style={{ color: '#8e24aa' }}
                                >
                                    Change Email Address
                                </Button>
                            </div>
                            
                            <div className="mt-4">
                                <small className="text-muted">
                                    <Spinner animation="grow" size="sm" className="me-2" />
                                    Waiting for verification...
                                </small>
                            </div>
                        </Container>
                    )}
                </Container>
            </Container>
        </div>
    );
};

